import React, { useState, useEffect, useRef } from 'react';
import {
    Input,
    Button,
    Space,
    Typography,
    message,
    Row,
    Col,
    Spin,
    Card
} from 'antd';
import { ReloadOutlined, LockOutlined } from '@ant-design/icons';
import * as adminactions from "store/actions/AuthActions";

const { Title, Text } = Typography;

const OTPInput = ({
    email,
    onVerify,
    onBack,
    title = "Enter OTP",
    subtitle = "We've sent a 6-digit code to your email",
    otpType = "login",
    embedded = false
}) => {
    const [otp, setOtp] = useState(['', '', '', '', '', '']);
    const [loading, setLoading] = useState(false);
    const [resendLoading, setResendLoading] = useState(false);
    const [countdown, setCountdown] = useState(0);
    const [error, setError] = useState('');
    const inputRefs = useRef([]);

    useEffect(() => {
        let timer;
        if (countdown > 0) {
            timer = setTimeout(() => setCountdown(countdown - 1), 1000);
        }
        return () => clearTimeout(timer);
    }, [countdown]);

    useEffect(() => {
        if (inputRefs.current[0]) {
            inputRefs.current[0].focus();
        }
    }, []);

    const handleInputChange = (index, value) => {
        if (!/^\d*$/.test(value)) return;

        const newOtp = [...otp];
        newOtp[index] = value;
        setOtp(newOtp);
        setError('');

        if (value && index < 5) {
            inputRefs.current[index + 1]?.focus();
        }

        if (newOtp.every(digit => digit !== '') && newOtp.join('').length === 6) {
            handleVerifyOTP(newOtp.join(''));
        }
    };

    const handleKeyDown = (index, e) => {
        if (e.key === 'Backspace' && !otp[index] && index > 0) {
            inputRefs.current[index - 1]?.focus();
        }
        if (e.key === 'v' && (e.ctrlKey || e.metaKey)) {
            e.preventDefault();
            navigator.clipboard.readText().then(text => {
                const digits = text.replace(/\D/g, '').slice(0, 6);
                if (digits.length === 6) {
                    const newOtp = digits.split('');
                    setOtp(newOtp);
                    handleVerifyOTP(digits);
                }
            });
        }
    };

    const handleVerifyOTP = async (otpCode = otp.join('')) => {
        if (otpCode.length !== 6) {
            setError('Please enter all 6 digits');
            return;
        }

        setLoading(true);
        setError('');

        try {
            let response;
            if (otpType === 'login') {
                response = await adminactions.verifyLoginOTP(email, otpCode);
            } else if (otpType === 'password_reset') {
                response = await adminactions.verifyPasswordResetOTP(email, otpCode);
            }

            if (response && !response.message?.includes('Invalid') && !response.message?.includes('expired')) {
                message.success('OTP verified successfully!');
                onVerify(response, otpCode);
            } else {
                setError(response.message || 'Invalid OTP. Please try again.');
                setOtp(['', '', '', '', '', '']);
                inputRefs.current[0]?.focus();
            }
        } catch (err) {
            setError('Verification failed. Please try again.');
            setOtp(['', '', '', '', '', '']);
            inputRefs.current[0]?.focus();
        } finally {
            setLoading(false);
        }
    };

    const handleResendOTP = async () => {
        setResendLoading(true);
        try {
            const response = await adminactions.resendOTP(email, otpType);
            if (response && !response.message?.includes('Error')) {
                message.success('OTP sent successfully!');
                setCountdown(60);
                setOtp(['', '', '', '', '', '']);
                setError('');
                inputRefs.current[0]?.focus();
            } else {
                message.error(response.message || 'Failed to resend OTP');
            }
        } catch (err) {
            message.error('Failed to resend OTP');
        } finally {
            setResendLoading(false);
        }
    };

    const clearOTP = () => {
        setOtp(['', '', '', '', '', '']);
        setError('');
        inputRefs.current[0]?.focus();
    };

    const maskedEmail = email.replace(/(.{2})(.*)(@.*)/, '$1***$3');

    const content = (
        <>
            <div className="reset-header">
                <div className="reset-icon-circle">
                    <LockOutlined />
                </div>
                <Title level={3} style={{ margin: '0 0 8px 0', color: '#1a1a2e' }}>
                    {title}
                </Title>
                <Text style={{ color: '#64748b', fontSize: 14, display: 'block', marginBottom: 4 }}>
                    {subtitle}
                </Text>
                <Text style={{ color: '#0050b3', fontSize: 14, fontWeight: 500 }}>
                    {maskedEmail}
                </Text>
            </div>

            <Spin spinning={loading}>
                <Row gutter={8} justify="center" style={{ marginBottom: 24 }}>
                    {otp.map((digit, index) => (
                        <Col key={index}>
                            <Input
                                ref={ref => inputRefs.current[index] = ref}
                                value={digit}
                                onChange={e => handleInputChange(index, e.target.value)}
                                onKeyDown={e => handleKeyDown(index, e)}
                                maxLength={1}
                                className={`otp-digit-input ${error ? 'otp-error' : ''}`}
                            />
                        </Col>
                    ))}
                </Row>
            </Spin>

            {error && (
                <div className="login-error">
                    {error}
                </div>
            )}

            <Space direction="vertical" style={{ width: '100%' }} size="middle">
                <Button
                    type="primary"
                    size="large"
                    block
                    onClick={() => handleVerifyOTP()}
                    loading={loading}
                    disabled={otp.some(digit => digit === '')}
                    className="login-submit-btn"
                >
                    Verify OTP
                </Button>

                <div style={{ textAlign: 'center' }}>
                    <Text style={{ color: '#64748b', fontSize: 14 }}>
                        Didn't receive the code?
                    </Text>
                    <br />
                    <Button
                        type="link"
                        onClick={handleResendOTP}
                        loading={resendLoading}
                        disabled={countdown > 0}
                        icon={<ReloadOutlined />}
                        className="login-link"
                    >
                        {countdown > 0 ? `Resend in ${countdown}s` : 'Resend OTP'}
                    </Button>
                </div>

                <div style={{ textAlign: 'center' }}>
                    <Button type="link" onClick={clearOTP} className="login-link" style={{ marginRight: 16 }}>
                        Clear
                    </Button>
                    <Button type="link" onClick={onBack} className="login-link">
                        Back
                    </Button>
                </div>
            </Space>
        </>
    );

    if (embedded) {
        return content;
    }

    // Standalone mode (fallback)
    return (
        <div style={{
            display: 'flex',
            justifyContent: 'center',
            alignItems: 'center',
            minHeight: '100vh',
            background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
            padding: '20px'
        }}>
            <Card
                style={{
                    width: '100%',
                    maxWidth: 450,
                    boxShadow: '0 10px 25px rgba(0,0,0,0.1)',
                    borderRadius: 16,
                    border: 'none'
                }}
                bodyStyle={{ padding: '40px 32px' }}
            >
                {content}
            </Card>
        </div>
    );
};

export default OTPInput;
